import React, { useState, useRef, useEffect, useMemo, KeyboardEvent } from 'react';
import { styled } from '../../styles/stitches.config';
import { FiChevronDown } from 'react-icons/fi';
import type { OptionGroup } from '../register/StyledDropdown';

/**
 * A single combobox used everywhere a subject/level is picked: it is BOTH an
 * autocomplete (type to filter) AND a dropdown (click the chevron to open the
 * full list). Works with a flat `string[]` or grouped `OptionGroup[]`, and in
 * `single` (one value) or `multi` (add-to-a-list) mode.
 */

type FlatOrGrouped = string[] | OptionGroup[];

interface SubjectComboboxProps {
  options: FlatOrGrouped;
  /** 'single' binds one value; 'multi' fires onSelect to add to a parent list. */
  mode?: 'single' | 'multi';
  /** single mode: the currently selected value (shown when the box is closed). */
  value?: string;
  /** Called when the user picks an option (or types free text if allowed). */
  onSelect: (value: string) => void;
  /** multi mode: values already chosen, hidden from the list. */
  exclude?: string[];
  placeholder?: string;
  /** Allow committing typed text that doesn't match any option. */
  allowFreeText?: boolean;
  disabled?: boolean;
}

function toGroups(options: FlatOrGrouped): OptionGroup[] {
  if (options.length === 0) return [];
  if (typeof options[0] === 'string') {
    return [{ label: '', options: (options as string[]).map(v => ({ value: v, label: v })) }];
  }
  return options as OptionGroup[];
}

const Wrap = styled('div', { position: 'relative', width: '100%', minWidth: 0 });

const ControlRow = styled('div', {
  boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: '0.25rem',
  width: '100%', border: '1px solid $elementBorder', borderRadius: '0.5rem',
  padding: '0 0.5rem 0 0.75rem', height: '2.75rem', backgroundColor: 'white',
  '&:focus-within': { borderColor: '$blue8' },
  variants: { disabled: { true: { backgroundColor: '$slate2', cursor: 'not-allowed' } } },
});

const Input = styled('input', {
  all: 'unset', flex: 1, minWidth: 0, fontSize: '$sm', height: '100%',
  '&::placeholder': { color: '$slate9' },
});

const Chevron = styled('button', {
  all: 'unset', display: 'flex', alignItems: 'center', flexShrink: 0,
  cursor: 'pointer', color: '$slate10', padding: '0.25rem',
  transition: 'transform 0.15s ease',
  variants: { open: { true: { transform: 'rotate(180deg)' } } },
});

const Panel = styled('div', {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 999,
  maxHeight: '15rem', overflowY: 'auto', backgroundColor: 'white',
  border: '1px solid $elementBorder', borderRadius: '0.5rem',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '0.25rem',
});

const GroupLabel = styled('div', {
  padding: '0.5rem 0.5rem 0.25rem', fontSize: '0.75rem', fontWeight: 'bold', color: '$slate10',
});

const OptionItem = styled('div', {
  padding: '0.5rem', borderRadius: '0.375rem', fontSize: '$sm', cursor: 'pointer',
  whiteSpace: 'normal', wordBreak: 'break-word',
  variants: { active: { true: { backgroundColor: '$elementBackgroundHover' } } },
});

const Empty = styled('div', { padding: '0.6rem 0.5rem', fontSize: '$sm', color: '$slate9' });

const SubjectCombobox: React.FC<SubjectComboboxProps> = ({
  options, mode = 'single', value = '', onSelect, exclude = [],
  placeholder = 'Search or select…', allowFreeText = false, disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resolve the display label for the currently selected single value.
  const selectedLabel = useMemo(() => {
    if (mode !== 'single' || !value) return '';
    const found = toGroups(options).flatMap(g => g.options).find(o => o.value === value);
    return found?.label ?? value;
  }, [options, value, mode]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return toGroups(options)
      .map(g => ({
        label: g.label,
        options: g.options.filter(o =>
          !exclude.includes(o.value) && (q === '' || o.label.toLowerCase().includes(q)),
        ),
      }))
      .filter(g => g.options.length > 0);
  }, [options, query, exclude]);

  const flat = useMemo(() => groups.flatMap(g => g.options), [groups]);

  // Keep the highlight in range as the filtered list changes.
  useEffect(() => { setActive(0); }, [query, open]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const commit = (val: string) => {
    if (!val) return;
    onSelect(val);
    setQuery('');
    setOpen(false);
    if (mode === 'single') inputRef.current?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive(a => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const candidate = flat[active] ?? flat[0];
      if (candidate) { e.preventDefault(); commit(candidate.value); }
      else if (allowFreeText && query.trim()) { e.preventDefault(); commit(query.trim()); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = open ? query : selectedLabel;

  return (
    <Wrap ref={wrapRef}>
      <ControlRow disabled={disabled} onClick={() => { if (!disabled) { setOpen(true); inputRef.current?.focus(); } }}>
        <Input
          ref={inputRef}
          value={displayValue}
          placeholder={mode === 'single' && selectedLabel ? selectedLabel : placeholder}
          disabled={disabled}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <Chevron
          type="button"
          open={open}
          tabIndex={-1}
          aria-label="Toggle options"
          onClick={e => { e.stopPropagation(); if (disabled) return; setOpen(o => !o); inputRef.current?.focus(); }}
        >
          <FiChevronDown />
        </Chevron>
      </ControlRow>

      {open && (
        <Panel onMouseDown={e => e.preventDefault()}>
          {flat.length === 0 ? (
            <Empty>{allowFreeText && query.trim() ? `Press Enter to add “${query.trim()}”` : 'No matches'}</Empty>
          ) : (
            groups.map(group => (
              <div key={group.label || '_'}>
                {group.label && <GroupLabel>{group.label}</GroupLabel>}
                {group.options.map(opt => {
                  const idx = flat.findIndex(o => o.value === opt.value);
                  return (
                    <OptionItem
                      key={opt.value}
                      active={idx === active}
                      title={opt.label}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => commit(opt.value)}
                    >
                      {opt.label}
                    </OptionItem>
                  );
                })}
              </div>
            ))
          )}
        </Panel>
      )}
    </Wrap>
  );
};

export default SubjectCombobox;
